/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { OrderItemsDaoService } from '../order-item-dao.service';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { OrderItem } from 'src/app/_model/order-item';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { IMSId } from 'src/app/_model/ims-object';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { OrderItemDbTableDefinition } from './order-item-db-table-definition';
import { LocalOrderItemDbTableDefinition } from './local-order-item-db-table-definition';

@Injectable()
export class OrderItemsDbDaoService implements OrderItemsDaoService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private localStatusService: LocalStatusService,
    private appParameter: AppParameterService,
    private orderItemTableDefinition: OrderItemDbTableDefinition,
    private localOrderItemTableDefinition: LocalOrderItemDbTableDefinition
  ) {}

  public async saveOrderItems(orderItems: OrderItem[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var orderItemsQueries: Query[] = [];
      for (const orderItem of orderItems) {
        let orderItemQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (orderItem.isLocal()
              ? this.localOrderItemTableDefinition.tableName
              : this.orderItemTableDefinition.tableName) +
            '(id, label, databag, localStorageInfo)' + // 4
            ' VALUES (?,?,?,?)',
          parameters: [
            Number(
              orderItem.isLocal()
                ? orderItem.id.secondaryId
                : orderItem.id.imsId
            ),
            orderItem.label,
            orderItem.databag ? JSON.stringify(orderItem.databag) : undefined,
            orderItem.localStorageInfo,
          ],
        };

        await this.localStatusService.storeLocalOrderItemStatus(orderItem);
        orderItemsQueries.push(orderItemQuery);
      }
      await this.dbPersistenceService.executeStatements(db, orderItemsQueries);
    } catch (error) {
      this.logger.log(
        'Error saving order items to db',
        error,
        '-',
        error.message
      );
    }
  }

  public async getOrderItems(): Promise<OrderItem[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let orderItems: OrderItem[] = [];

    try {
      // Get Synced OrderItems
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.orderItemTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        let localStatus =
          await this.localStatusService.loadLocalOrderItemStatus({
            imsId: row['id'],
            secondaryId: undefined,
            objRef: 'ims/OrderItem',
          });
        orderItems.push(
          OrderItem.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              localStorageInfo: row['localStorageInfo'],
            },
            localStatus,
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/OrderItem')
          )
        );
      }
    } catch (error) {
      this.logger.log('Error reading order items from db', error);
    }

    try {
      // Get local OrderItems
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localOrderItemTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        const localOrderItem = OrderItem.fromDto(
          {
            id: undefined,
            secondaryid: row['id'],
            label: row['label'],
            localStorageInfo: row['localStorageInfo'],
          },
          {},
          row['databag'] ? JSON.parse(row['databag']) : undefined,
          this.appParameter.getFormatLabelForObject('ims/OrderItem')
        );
        orderItems.push(localOrderItem);
      }
    } catch (error) {
      this.logger.log('Error reading order items from db', error);
    }

    return orderItems;
  }

  public async createOrderItem(orderItem: OrderItem): Promise<OrderItem> {
    this.logger.log(this.constructor.name, 'create orderItem:', orderItem);
    try {
      let db = await this.dbPersistenceService.getImsDb();

      let orderItemQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          this.localOrderItemTableDefinition.tableName +
          '(label, databag)' + // 3
          ' VALUES (?,?)',
        parameters: [
          orderItem.label,
          orderItem.databag ? JSON.stringify(orderItem.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, orderItemQuery);
      let res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        orderItem.id.secondaryId = res.values.shift()?.id;
        this.logger.log('Updated orderItem secondary id', orderItem);
      }
      return orderItem;
    } catch (error) {
      this.logger.log(
        'Error saving orderItem to db',
        error,
        '-',
        error?.message
      );
      return undefined;
    }
  }

  public async deleteLocalOrderItem(id: IMSId): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.localOrderItemTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [id.secondaryId],
      });
    } catch (error) {
      this.logger.log('Error deleting local orderitem', id, 'from db', error);
    }
  }

  public async reset() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localOrderItemTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localOrderItemTableDefinition.tableName
    );
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.orderItemTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.orderItemTableDefinition.tableName
    );
  }
}
